import type { ComponentContentSchema } from '@vibesites/runtime';
import { usePropertyField, useContentToken, EditableToken } from '@vibesites/runtime/react';

/**
 * Accessibility page body. The stripe below the hero wave band is reserved
 * entirely for the AudioEye accessibility-statement portal — no hand-authored
 * copy or CTA — so the statement stays authoritative and always reflects the
 * site's live AudioEye status (matches the reference site).
 *
 * The only non-AudioEye element is a visually-hidden heading: it stays out of
 * the visible stripe but gives assistive tech a labelled landmark for the
 * embed, and sits under the visible page H1 supplied by HeroImageBG as an h2.
 */
export function AccessibilityStatement() {
  const heading = useContentToken('accessibilitystatement.heading');
  const domain = (usePropertyField('property_domain') as string) || '';

  const iframeSrc = domain
    ? `https://portal.audioeye.com/accessibility-statement.html?domain=${encodeURIComponent(domain)}`
    : '';

  return (
    <section className="bg-[var(--vibe-color-background)] py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="accessibilitystatement.heading"
          as="h2"
          className="sr-only"
        >
          {heading}
        </EditableToken>

        {iframeSrc && (
          <iframe
            src={iframeSrc}
            title="Accessibility Statement"
            className="w-full border-0"
            style={{ height: '2300px' }}
          />
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AccessibilityStatement',
  displayName: 'Accessibility Statement',
  tokens: {
    'accessibilitystatement.heading': {
      label: 'Section Heading (screen-reader only)',
      type: 'text',
      defaultValue: 'Accessibility Statement',
    },
  },
};

(AccessibilityStatement as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AccessibilityStatement;
